import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

interface SearchBarProps {
  onClose: () => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();
  
  useEffect(() => {
    // Focus the input when search bar is opened
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Add event listener for Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (query.trim().length > 0) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      onClose();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center pt-20 z-50 animate-fade-in">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-serif font-bold">{t('search_legal_nest')}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-neutral-100 transition-colors"
            aria-label={t('close')}
          >
            <X size={24} className="text-neutral-700" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search_placeholder')}
            className="form-input text-lg"
          />
          <div className="mt-4 flex justify-end">
            <button type="submit" className="btn btn-primary">
              {t('search')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SearchBar;